import { useMemo, useState } from 'react';
import { Badge, Form, InputGroup } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

import useAdvanceTable from '../../hooks/useAdvanceTable';
import AdvanceTableProvider from '../../providers/AdvanceTableProvider';
import AdvanceTable from '../base/AdvanceTable';
import AdvanceTableFooter from '../base/AdvanceTableFooter';
import { COVERAGE_BADGE, coverageFrom } from '../../helpers/orderCoverage';
import { ORIGIN, fateOf } from '../../helpers/ordersAnalytics';
import { clientColour } from '../../helpers/clientColour';
import { formatLength, exactLength } from '../../helpers/formatLength';

/**
 * Every order, wherever it currently lives.
 *
 * Open, delivered and deleted orders are three different collections in two
 * different databases, and until now the only way to see them together was to
 * open three screens and hold the comparison in your head. They are one table
 * here because the question people actually ask — "what did we do for this
 * customer last year" — does not care which store an order happens to sit in.
 *
 * The fate bar is the point of the row: an order that is 100% covered by a
 * promise and an order that is 100% made read identically as a percentage, and
 * only one of them is safe to tell a customer about.
 */

const when = value => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? '—'
    : new Intl.DateTimeFormat('ro-RO', { dateStyle: 'medium' }).format(d);
};

/** The row's metres, as a bar. Same colours as the doughnut above it. */
const FateBar = ({ row }) => {
  const f = fateOf(row);
  if (f.ordered <= 0) return <span className="text-body-tertiary">—</span>;

  const w = v => `${(v / f.ordered) * 100}%`;

  return (
    <div style={{ minWidth: 120 }}>
      <div
        className="d-flex rounded overflow-hidden border"
        style={{ height: 6 }}
        title={`made ${exactLength(f.produced)} · from stock ${exactLength(
          f.stock
        )} · promised ${exactLength(f.promised)} · nothing yet ${exactLength(
          f.open
        )}`}
      >
        <div className="bg-success" style={{ width: w(f.produced) }} />
        <div className="bg-primary" style={{ width: w(f.stock) }} />
        <div className="bg-info" style={{ width: w(f.promised) }} />
        <div className="bg-warning" style={{ width: w(f.open) }} />
      </div>
      <span className="fs-10 text-body-tertiary">
        {Math.round(((f.produced + f.stock) / f.ordered) * 100)}% delivered
      </span>
    </div>
  );
};

const columns = [
  {
    id: 'origin',
    header: 'Where',
    accessorFn: row => ORIGIN[row.origin]?.label || row.origin,
    cell: ({ row: { original } }) => {
      const o = ORIGIN[original.origin] || { label: original.origin, color: 'secondary' };
      return (
        <Badge bg={o.color} variant="phoenix" className="fs-10">
          {o.label}
        </Badge>
      );
    },
    meta: {
      headerProps: { style: { width: '9%' } },
      cellProps: { className: 'py-2' }
    }
  },
  {
    id: 'orderCode',
    header: 'Order',
    accessorFn: row => row.orderCode || row.orderId || '',
    cell: ({ row: { original } }) =>
      original.origin === 'open' && original.orderId ? (
        <Link
          to={`/apps/sales-management/order-details/${original.orderId}`}
          className="fw-semibold fs-9"
          onClick={e => e.stopPropagation()}
        >
          {original.orderCode || original.orderId}
        </Link>
      ) : (
        <span className="fw-semibold fs-9">
          {original.orderCode || original.orderId || '—'}
        </span>
      ),
    meta: {
      headerProps: { style: { minWidth: 160 } },
      cellProps: { className: 'py-2' }
    }
  },
  {
    id: 'client',
    header: 'Customer',
    accessorFn: row => row.client || '',
    cell: ({ row: { original } }) =>
      original.client ? (
        <Badge
          bg={clientColour(original.client)}
          variant="phoenix"
          className="fs-10"
        >
          {original.client}
        </Badge>
      ) : (
        <span className="text-body-tertiary">—</span>
      ),
    meta: {
      headerProps: { style: { minWidth: 130 } },
      cellProps: { className: 'py-2' }
    }
  },
  {
    id: 'createdAt',
    header: 'Placed',
    accessorFn: row => row.createdAt || 0,
    cell: ({ row: { original } }) => (
      <span className="fs-9">{when(original.createdAt)}</span>
    ),
    meta: {
      headerProps: { style: { width: '10%' } },
      cellProps: { className: 'py-2 white-space-nowrap' }
    }
  },
  {
    id: 'dueDate',
    header: 'Due',
    accessorFn: row => (row.dueDate ? new Date(row.dueDate).getTime() : 0),
    cell: ({ row: { original } }) => (
      <span className="fs-9">{when(original.dueDate)}</span>
    ),
    meta: {
      headerProps: { style: { width: '10%' } },
      cellProps: { className: 'py-2 white-space-nowrap' }
    }
  },
  {
    id: 'closedAt',
    header: 'Closed',
    accessorFn: row => row.closedAt || 0,
    cell: ({ row: { original } }) =>
      original.closedAt ? (
        <span className="fs-9">{when(original.closedAt)}</span>
      ) : (
        <span className="text-body-tertiary">—</span>
      ),
    meta: {
      headerProps: { style: { width: '10%' } },
      cellProps: { className: 'py-2 white-space-nowrap' }
    }
  },
  {
    id: 'ordered',
    header: 'Ordered',
    accessorFn: row => Number(row.ordered) || 0,
    cell: ({ row: { original } }) => (
      <span className="fw-semibold fs-9" title={exactLength(original.ordered)}>
        {formatLength(original.ordered)}
      </span>
    ),
    meta: {
      headerProps: { style: { width: '10%' }, className: 'text-end' },
      cellProps: { className: 'text-end py-2 white-space-nowrap' }
    }
  },
  {
    id: 'fate',
    header: 'What happened to it',
    enableSorting: false,
    cell: ({ row: { original } }) => <FateBar row={original} />,
    meta: {
      headerProps: { style: { minWidth: 140 } },
      cellProps: { className: 'py-2' }
    }
  },
  {
    id: 'state',
    header: 'State',
    accessorFn: row =>
      row.origin === 'deleted' ? 'deleted' : coverageFrom(row).state,
    cell: ({ row: { original } }) => {
      if (original.origin === 'deleted') {
        return (
          <div>
            <Badge bg="danger" variant="phoenix" className="fs-10">
              Deleted
            </Badge>
            {original.reason && (
              <div className="fs-10 text-body-tertiary mt-1">
                {original.reason}
              </div>
            )}
          </div>
        );
      }
      const b = COVERAGE_BADGE[coverageFrom(original).state] || {
        label: '—',
        color: 'secondary'
      };
      return (
        <Badge bg={b.color} variant="phoenix" className="fs-10">
          {b.label}
        </Badge>
      );
    },
    meta: {
      headerProps: { style: { minWidth: 130 } },
      cellProps: { className: 'py-2' }
    }
  }
];

const OrdersAnalyticsTable = ({ rows = [] }) => {
  const [search, setSearch] = useState('');

  // Matched here rather than through the table's global filter because the
  // useful fields are not all rendered as text — a product key never appears in
  // a cell, and somebody hunting "MYYM 3x2.5" is searching by what was ordered,
  // not by what the row happens to show.
  const data = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      [r.orderCode, r.orderId, r.client, ...(r.products || [])]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const table = useAdvanceTable({
    data,
    columns,
    pageSize: 15,
    pagination: true,
    sortable: true
  });

  return (
    <>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h3 className="text-1100 mb-0">Every order</h3>
          <p className="text-700 fs-9 mb-0">
            Open, delivered and deleted, in one place.
          </p>
        </div>
        <InputGroup style={{ maxWidth: 280 }}>
          <InputGroup.Text className="bg-body">
            <FontAwesomeIcon icon={faMagnifyingGlass} className="fs-10" />
          </InputGroup.Text>
          <Form.Control
            size="sm"
            placeholder="order, customer, product…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </InputGroup>
      </div>

      <AdvanceTableProvider {...table}>
        <AdvanceTable
          tableProps={{ className: 'phoenix-table border-top border-200 fs-9' }}
          rowClassName="hover-actions-trigger align-middle"
        />
        <AdvanceTableFooter pagination className="py-3" />
      </AdvanceTableProvider>
    </>
  );
};

export default OrdersAnalyticsTable;
